import React, {FC, ReactNode, useEffect, useRef} from 'react';
import styled from 'src/theme';
import useMediaQuery from 'use-media-antd-query';
import {defaultColors} from 'src/parameters/colors';
import {initRoofsData} from 'src/store/roofData/initRoofsData';
import {IRoofStoreModule, StepType} from 'src/store/RoofStoreModule';

import {useStoreon} from 'storeon/react';
import {IEvents, IState} from 'src/store';

import {Col, Row} from "antd";

import {Button} from 'src/components/UI';

import {Header} from "../../parts/Header";
import {StepHeader} from "./StepHeader";
import {StepNav} from "./StepNav";

import {roofCards, usePresenter} from "./usePresenter";
import Copyright from "../../Copyright/Copyright";
import {drawAllDimensions, drawSingleDimension} from "src/components/Roof/utils/drawDimensions";

interface Props {
  children: ReactNode;
}

const stepHeaders = {
  1: 'Выберите тип кровли',
  2: 'Выберите размеры',
  3: 'Выберите профиль',
  4: 'Выберите цвет',
  5: 'Выберите покрытие',
  6: 'Расчетный лист',
};

export const Layout: FC<Props> = ({ children }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const presenter = usePresenter();

  const colSize = useMediaQuery();
  const isMobile = colSize === 'xs' || colSize === 'sm' || colSize === 'md';

  const { dispatch, roofStore } = useStoreon<IState, IEvents>('roofStore');

  const handleNext = () => {
    if (roofStore.stepID === 2) {
      const { fields } = initRoofsData[roofStore.roofType];

      const emptyFieldDetected = fields.find(field => {
        const fieldValue = roofStore[field.name + 'Value' as keyof IRoofStoreModule];
        return fieldValue === null;
      });

      if (emptyFieldDetected) {
        alert('Пожалуйста заполните все поля!');
      } else {
        dispatch('roof/set-stepID', { stepID: 3 })
      }
    } else {
      dispatch('roof/set-stepID', { stepID: ((roofStore.stepID + 1) as StepType) })
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;
    const context = canvas.getContext('2d');
    if (context == null) return;

    if (roofStore.stepID === 2) {
      if (presenter.focusedField !== null) {
        drawSingleDimension(canvas, presenter.roofType, presenter.focusedField);
      } else {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
    } else if (roofStore.stepID === 6) {
      drawAllDimensions(canvas, presenter.roofType, roofStore as unknown as Record<string, number | null>);
    } else {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [
    presenter.focusedField,
    presenter.roofType,
    roofStore.stepID,
    roofStore,
  ])

  return (
    <ContainerStyled>
      <Row>

        <Col xs={24}><Header children='Калькулятор кровли' /></Col>
        <Col xs={24}><StepHeader text={stepHeaders[roofStore.stepID]} /></Col>
        {roofStore.stepID === 1 ? (
            <>
              <Col xs={24}><StepNav /></Col>
              <Col xs={24}>{children}</Col>
            </>
          ) : (
            <>

              {isMobile && <StepNav />}
              <Col xs={{ span: 24, order: 2 }} lg={{ span: 11, order: 1 }}>

                <Row>
                  <Col xs={24}>
                    {!isMobile && <StepNav />}
                  </Col>
                </Row>

                  {children}
              </Col>

              <Col xs={{ span: 24, order: 1 }} lg={{ span: 12, offset: 1, order: 2 }}>
                <ImgWrapperStyled>
                  <div id={'img-wrap'} style={{position: 'relative'}}>
                    <canvas ref={canvasRef} id="imageView" width="1920px" height="1080px" style={{
                        zIndex: 9,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                    <ImgStyle colors={defaultColors} color_id={roofStore.colorID} src={presenter.roofImg} />
                  </div>
                  <ImgTitleStyle>{roofCards.find(card => card.type === roofStore.roofType)?.label}</ImgTitleStyle>
                </ImgWrapperStyled>
              </Col>
            </>
          )}

        {!(roofStore.stepID === 1 || roofStore.stepID === 6) &&
        <Col xs={24} lg={11} order={3} style={{ marginTop: '24px', textAlign: 'right'}}><ButtonStyled onClick={handleNext}>Далее</ButtonStyled></Col>}
        <Copyright />
      </Row>
    </ContainerStyled>
  );
};

const ButtonStyled = styled(Button)`
  width: 185px;
`;

const ContainerStyled = styled.div`
  width: 100%;
  min-width: 320px;
  max-width: 1660px;
  display: block;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 32px;
  position: relative;
  @media screen and ${props => props.theme.media.tablet} {
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 24px;
  }
  @media screen and ${props => props.theme.media.mobile} {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
  }
`;

const ImgWrapperStyled = styled.div`
  padding-bottom: 0;
  @media screen and ${props => props.theme.media.tablet} {
    padding-bottom: 40px;
  }
`;

const ImgStyle = styled.img<{ colors: any[], color_id: number }>`
  background-color: ${props => (props.colors.find(color => color.id === props.color_id)?.hexCode)}; 
  width: 100%;
`;

const ImgTitleStyle = styled.span`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
  text-transform: uppercase;
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 17px;
  line-height: 21px;
  color: ${props => props.theme.colors.nero};
  @media screen and ${props => props.theme.media.tablet} {}
`;
